// Account-aware date/time formatting.
//
// B-020 + localization config: on-screen dates must honour the account's
// configured LAYOUT (date_format), ZONE (timezone), and ERA (calendar:
// Gregorian or Buddhist) — not the browser's system locale. Components read
// these via useDateFormatter / useDateTimeFormatter (bound to account settings);
// this module holds the pure formatting so it's unit-testable.
//
// The settings UI offers three numeric layouts and four IANA zones
// (SettingsPage). Buddhist era = Gregorian + 543; we let Intl compute it via
// `calendar: 'buddhist'` so era edge cases are handled correctly.

export type DateFormatPattern = 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD'
export type CalendarSystem = 'gregorian' | 'buddhist'

export const DEFAULT_DATE_FORMAT: DateFormatPattern = 'DD/MM/YYYY'
export const DEFAULT_TIMEZONE = 'Asia/Bangkok'
export const DEFAULT_CALENDAR: CalendarSystem = 'gregorian'

export interface DateFormatOptions {
  /** Layout pattern. Defaults to DD/MM/YYYY. */
  format?: string
  /** IANA timezone (e.g. 'Asia/Bangkok'). Defaults to the runtime zone. */
  timezone?: string
  /** 'gregorian' (CE) or 'buddhist' (BE, +543). Defaults to gregorian. */
  calendar?: CalendarSystem
}

// Extract day / month / year (and optionally hour/minute) parts of a Date in
// the requested timezone + calendar, using Intl so era + zone are correct.
function partsOf(
  d: Date,
  timezone?: string,
  calendar?: CalendarSystem,
  withTime = false,
) {
  const dtf = new Intl.DateTimeFormat('en-US', {
    ...(timezone ? { timeZone: timezone } : {}),
    ...(calendar === 'buddhist' ? { calendar: 'buddhist' } : {}),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...(withTime ? { hour: '2-digit', minute: '2-digit', hour12: false } : {}),
  })
  const parts = dtf.formatToParts(d)
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? ''
  return {
    dd: get('day'),
    mm: get('month'),
    // Intl pads the Gregorian year to 4 digits; the Buddhist year is already
    // 4 digits (2569). Normalise any stray padding off the front.
    yyyy: get('year').replace(/^0+(?=\d)/, ''),
    hh: get('hour'),
    min: get('minute'),
  }
}

function layout(format: string, dd: string, mm: string, yyyy: string): string {
  switch (format) {
    case 'MM/DD/YYYY':
      return `${mm}/${dd}/${yyyy}`
    case 'YYYY-MM-DD':
      return `${yyyy}-${mm}-${dd}`
    case 'DD/MM/YYYY':
    default:
      return `${dd}/${mm}/${yyyy}`
  }
}

/**
 * Today's calendar date as 'YYYY-MM-DD' in the given IANA timezone.
 *
 * Retires the `new Date().toISOString().split('T')[0]` "today" pattern, which
 * reads the UTC calendar day and is wrong for zones ahead of UTC — it yields
 * YESTERDAY for Asia/Bangkok (UTC+7) between 00:00-07:00 local time. Falsy
 * timezone falls back to the runtime zone (see `partsOf`), matching the rest
 * of this module.
 */
export function localToday(timezone: string): string {
  const { dd, mm, yyyy } = partsOf(new Date(), timezone)
  return `${yyyy}-${mm}-${dd}`
}

/**
 * The account's local calendar date N months before "now", as 'YYYY-MM-DD',
 * read in the given IANA timezone.
 *
 * For report "last N months" date-range presets (DAT-1): the old
 * `new Date(); d.setMonth(d.getMonth() - N); d.toISOString().slice(0, 10)`
 * pattern read the UTC calendar day off a Date whose fields were set in
 * local time, which in a zone ahead of UTC (e.g. Asia/Bangkok, UTC+7) shifts
 * the result back a day during the first hours of the local day. This reads
 * "now"'s year/month/day in the account timezone via Intl, then does the
 * month subtraction with the runtime's own local Date field methods
 * (construct and read the same way, never crossing through toISOString) so
 * the round trip can't drift regardless of the runner's own timezone, the
 * same approach billingPeriod.ts's getMonthRange uses.
 */
export function localDateMonthsAgo(monthsAgo: number, timezone: string): string {
  const { dd, mm, yyyy } = partsOf(new Date(), timezone)
  // Day 1 of the target month first, so 31 May minus 3 months lands in
  // February instead of overflowing into March; then clamp the day.
  const first = new Date(Number(yyyy), Number(mm) - 1 - monthsAgo, 1)
  const lastDay = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate()
  const y = first.getFullYear()
  const m = String(first.getMonth() + 1).padStart(2, '0')
  const day = String(Math.min(Number(dd), lastDay)).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/**
 * The calendar day encoded in a Date's UTC representation, as 'YYYY-MM-DD'.
 *
 * Only safe on a Date anchored to UTC midnight of the intended day: one
 * parsed from a date-only 'YYYY-MM-DD' string (JS parses those as UTC
 * midnight per spec, so a date-only server field like `installed_at` or
 * `due_date` round-trips exactly) or from a `${...}T00:00:00Z` literal,
 * optionally after pure calendar-field arithmetic (`setDate`/`setMonth`/
 * `setFullYear`) on such a Date - those preserve the wall-clock time, so the
 * UTC day shifts by exactly the requested delta regardless of the runtime's
 * own timezone.
 *
 * Calling this on a fresh `new Date()` "now" reading is the DAT-1 bug: it
 * reads the UTC calendar day, which is the wrong day for a zone ahead of UTC
 * during the first hours of its local day. Use `localToday` for "now".
 */
export function utcDateOnly(date: Date): string {
  return date.toISOString().slice(0, 10)
}

/**
 * The account's local calendar date N years after "now", as 'YYYY-MM-DD',
 * read in the given IANA timezone. Clamps 29 February onto 28 when the
 * target year is not a leap year.
 *
 * Used as the default upper bound on date inputs (T149-089/T149-090):
 * "today + 2 years" in the account's own timezone, not the runtime's.
 */
export function localDateYearsAhead(yearsAhead: number, timezone: string): string {
  const { dd, mm, yyyy } = partsOf(new Date(), timezone)
  const targetYear = Number(yyyy) + yearsAhead
  const month = Number(mm)
  const lastDayOfMonth = new Date(targetYear, month, 0).getDate()
  const day = Math.min(Number(dd), lastDayOfMonth)
  return `${targetYear}-${mm}-${String(day).padStart(2, '0')}`
}

/**
 * The account's local calendar date N days after "now", as 'YYYY-MM-DD', read
 * in the given IANA timezone.
 *
 * EP-150 (App Review 5.1.1v): a preview of "erased for good on <date>" before
 * the account-deletion request has been sent. The server hasn't stamped a
 * real `scheduled_at` yet, so the client shows today + `grace_days` in the
 * account's own zone. Reads today's local fields via Intl, then does the day
 * arithmetic with the runtime's own local Date field methods (never crossing
 * through `toISOString`), the same approach `localDateMonthsAgo`/
 * `localDateYearsAhead` use.
 */
export function localDateDaysAhead(daysAhead: number, timezone: string): string {
  const { dd, mm, yyyy } = partsOf(new Date(), timezone)
  const target = new Date(Number(yyyy), Number(mm) - 1, Number(dd))
  target.setDate(target.getDate() + daysAhead)
  const y = target.getFullYear()
  const m = String(target.getMonth() + 1).padStart(2, '0')
  const day = String(target.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/**
 * Format an ISO date string per the account's layout / zone / calendar.
 * Returns '' for null/empty/invalid input so callers can render it directly.
 */
export function formatDate(
  dateString: string | null | undefined,
  options: DateFormatOptions = {},
): string {
  if (!dateString) return ''
  const d = new Date(dateString)
  if (Number.isNaN(d.getTime())) return ''
  const { format = DEFAULT_DATE_FORMAT, timezone, calendar } = options
  const { dd, mm, yyyy } = partsOf(d, timezone, calendar)
  return layout(format, dd, mm, yyyy)
}

/**
 * Like formatDate but appends 24-hour HH:MM in the account's timezone.
 * Used for audit-style datetime fields (signed at, expires at, recorded at).
 */
export function formatDateTime(
  dateString: string | null | undefined,
  options: DateFormatOptions = {},
): string {
  if (!dateString) return ''
  const d = new Date(dateString)
  if (Number.isNaN(d.getTime())) return ''
  const { format = DEFAULT_DATE_FORMAT, timezone, calendar } = options
  const { dd, mm, yyyy, hh, min } = partsOf(d, timezone, calendar, true)
  return `${layout(format, dd, mm, yyyy)} ${hh}:${min}`
}

/**
 * Format just the 24-hour HH:MM of an ISO datetime in the account's timezone
 * (no date). Used for time-of-day columns (e.g. parking entry/exit). Returns
 * '' for null/empty/invalid input. Honours the configured zone, not the
 * browser's — B-020.
 */
export function formatTime(
  dateString: string | null | undefined,
  options: Pick<DateFormatOptions, 'timezone'> = {},
): string {
  if (!dateString) return ''
  const d = new Date(dateString)
  if (Number.isNaN(d.getTime())) return ''
  const dtf = new Intl.DateTimeFormat('en-US', {
    ...(options.timezone ? { timeZone: options.timezone } : {}),
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
  const parts = dtf.formatToParts(d)
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? ''
  // Some Intl implementations render midnight as "24" with hour12:false —
  // normalise it to "00" so the hour is always a 0–23 value.
  const hour = get('hour') === '24' ? '00' : get('hour')
  return `${hour}:${get('minute')}`
}

/**
 * Numeric year / 1-indexed month / day / 24-hour hour / minute of an ISO
 * datetime, read in the given IANA timezone — for callers doing arithmetic
 * on the zoned wall-clock reading (e.g. minutes-since-midnight for a
 * time-grid position, or same-day comparisons), not just formatting it for
 * display. `formatTime`/`formatDateTime` above cover the display case;
 * this exists because that arithmetic needs numbers, not a formatted
 * string. Falsy timezone falls back to the runtime zone, matching the rest
 * of this module. Returns all-zero for invalid input.
 */
export function zonedDateTimeParts(
  dateString: string,
  timezone?: string,
): { year: number; month: number; day: number; hour: number; minute: number } {
  const d = new Date(dateString)
  if (Number.isNaN(d.getTime())) return { year: 0, month: 0, day: 0, hour: 0, minute: 0 }
  const dtf = new Intl.DateTimeFormat('en-US', {
    ...(timezone ? { timeZone: timezone } : {}),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
  const parts = dtf.formatToParts(d)
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '0'
  // Some Intl implementations render midnight as "24" with hour12:false —
  // normalise it to 0, same as formatTime above.
  const hour = get('hour') === '24' ? 0 : Number(get('hour'))
  return {
    year: Number(get('year')),
    month: Number(get('month')),
    day: Number(get('day')),
    hour,
    minute: Number(get('minute')),
  }
}

export interface MonthYearOptions {
  /** BCP-47 locale for the month name (e.g. 'en', 'th'). Defaults to 'en'. */
  locale?: string
  /** 'gregorian' (CE) or 'buddhist' (BE, +543). Defaults to gregorian. */
  calendar?: CalendarSystem
}

/**
 * Format a long-month + year label (e.g. "June 2026" / "มิถุนายน 2569") honouring
 * the account's calendar era and UI locale — not the browser system locale.
 * Used for the billing-period filter dropdowns (Invoices + Payments).
 */
export function formatMonthYear(date: Date, options: MonthYearOptions = {}): string {
  if (Number.isNaN(date.getTime())) return ''
  const { locale = 'en', calendar } = options
  return new Intl.DateTimeFormat(locale, {
    month: 'long',
    year: 'numeric',
    // ALWAYS pass an explicit calendar — never omit it for the gregorian case.
    //
    // This function formats in the CALLER'S locale (unlike partsOf, which pins
    // 'en-US'), and a locale's DEFAULT calendar is not universally Gregorian:
    // in CLDR, 'th' defaults to BUDDHIST. So omitting the option made a
    // Gregorian-configured Thai account render Buddhist years —
    //   new Intl.DateTimeFormat('th', {month:'long',year:'numeric'})
    //     -> "สิงหาคม 2569"   (Buddhist, WRONG for a gregorian account)
    //   ... + {calendar:'gregory'}
    //     -> "สิงหาคม 2026"   (correct)
    // Found 2026-08-05 by CiC verification: the meter-readings month picker read
    // 2569 on an account whose Settings say Gregorian (CE). The invoice
    // generation billing-period picker (InvoiceGeneratePage.tsx:56) shared it.
    // 'gregory' is the BCP-47 identifier; 'gregorian' is the account-setting value.
    calendar: calendar === 'buddhist' ? 'buddhist' : 'gregory',
  }).format(date)
}

/**
 * Format a full day + long-month + year label (e.g. "22 September 2026" /
 * "22 กันยายน 2569") honouring the account's calendar era and UI locale, for
 * DateInput's words-echo caption (T149-090).
 *
 * Same trap as `formatMonthYear` above: ALWAYS pass an explicit calendar,
 * never omit it. `'th'` defaults to the Buddhist calendar in CLDR, so leaving
 * `calendar` out would render a Buddhist year even for a Gregorian-
 * configured Thai account. No `timezone` option, by design, matching
 * `formatMonthYear`: this formats a plain calendar date (no time
 * component), so the caller must construct `date` from local YYYY/MM/DD
 * components (`new Date(y, m - 1, d)`), never by parsing an ISO date-only
 * string (which parses as UTC midnight and can drift a day once formatted
 * back in a different zone).
 */
export function formatDateWords(date: Date, options: MonthYearOptions = {}): string {
  if (Number.isNaN(date.getTime())) return ''
  const { locale = 'en', calendar } = options
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    calendar: calendar === 'buddhist' ? 'buddhist' : 'gregory',
  }).format(date)
}
